import React from 'react';
import Button from '../ui/button';
import SectionBackground from '../sections/section-background';
import Breadcrumb from '../ui/breadcrumb';

interface HeroTemplateCandidatProps {
  title: string | React.ReactNode;
  /**
   * Petite ligne au-dessus du H1, en uppercase. Optionnelle.
   * Style aligné sur HeroTemplateEnts pour cohérence cross-pages.
   */
  eyebrow?: React.ReactNode;
  description: string | React.ReactNode;
  imageUrl: string;
  cta1?: {
    label: string;
    href?: string;
    onClick?: () => void;
  };
  cta2?: {
    label: string;
    href?: string;
    onClick?: () => void;
    variant?: 'secondary' | 'gradient' | 'white' | string;
  };
  page: string;
  section?: string;
  breadcrumbs?: {
    name: string;
    href: string;
  }[];
}

export default function HeroTemplateCandidat({
  title,
  eyebrow,
  description,
  imageUrl,
  cta1,
  cta2,
  page,
  section = 'hero',
  breadcrumbs,
}: HeroTemplateCandidatProps) {
  return (
    <SectionBackground
      page={page}
      section={section}
      className="relative overflow-hidden bg-gradient-to-br from-[#fff1f2] via-[#fef9c3] to-[#e0f2ff]"
    >
      {/* Image background à droite */}
      <div className="absolute inset-0 z-0 flex justify-end items-center pointer-events-none select-none">
        <img
          src={imageUrl}
          alt="Hero background"
          className="h-full max-h-[600px] object-contain md:pr-[5vw] opacity-10 md:opacity-80"
          fetchpriority="high"
          loading="eager"
          decoding="async"
        />
      </div>

      {/*
        Hero contraint à 1 écran sur 13 pouces — règle UX "une section
        = un écran". Densification des tailles + paddings compacts pour
        que le contenu (eyebrow + H1 + description + CTAs) tienne dans
        la zone visible sans forcer l'utilisateur à scroller.
      */}
      <section className="relative z-10 min-h-[88vh] flex items-center py-10 md:py-12 lg:py-14">
        <div className="w-full px-[5vw] xl:px-[100px] max-w-[1440px] mx-auto grid grid-cols-1 md:grid-cols-2 items-center gap-10">

          <div className="max-w-2xl">
            {/* Accroche au-dessus du H1 — petite, uppercase, discrète */}
            {eyebrow && (
              <p className="text-xs md:text-sm font-semibold text-neutral-500 mb-3 leading-snug uppercase tracking-widest">
                {eyebrow}
              </p>
            )}

            {breadcrumbs && (
              <div className="mb-4">
                <Breadcrumb items={breadcrumbs} />
              </div>
            )}

            {/* H1 — tailles densifiées pour tenir dans 1 écran 13",
                leading 1.1 + pb-1 pour ne pas clipper les descenders
                (g, p, j, y) avec bg-clip-text. */}
            <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-[3.5rem] font-title font-extrabold leading-[1.1] pb-1 text-transparent bg-clip-text bg-gradient-primary">
              {title}
            </h1>

            <div className="mt-5 mb-6 text-neutral-800 text-base md:text-lg leading-relaxed">
              {description}
            </div>

            {(cta1 || cta2) && (
              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4">
                {cta1 && (
                  <Button
                    variant="gradient"
                    size="lg"
                    href={cta1.href}
                    onClick={cta1.onClick}
                  >
                    {cta1.label}
                  </Button>
                )}

                {cta2 && (
                  <Button
                    variant={cta2.variant || "secondary"}
                    size="lg"
                    href={cta2.href}
                    onClick={cta2.onClick}
                    className="flex items-center justify-center"
                  >
                    {cta2.label}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </section>
    </SectionBackground>
  );
}
